export type SortKey = 'relevance' | 'name' | 'id';
export type SortDir = 'asc' | 'desc';
export type Density = 'card' | 'row' | 'table';

export interface IBrowseFilter {
    name: string;
    minLevel: number;
    maxLevel: number;
    ranks: number[];
    categories: number[];
    parts: number[];
    types: number[];
    sort: SortKey;
    dir: SortDir;
    density: Density;
}

export function emptyFilter(): IBrowseFilter {
    return {
        name: '', minLevel: 0, maxLevel: 100,
        ranks: [], categories: [], parts: [], types: [],
        sort: 'relevance', dir: 'desc', density: 'card',
    };
}

//  vue-router hands a repeated query param (`?g=1&g=2`) back as a string[],
//  not a string, even though callers here cast $route.query to
//  Record<string, string | undefined>. Take the last value, matching typical
//  query semantics (the last occurrence wins), rather than letting a raw
//  array reach .split() below and throw.
const single = (v: string | string[] | undefined): string | undefined =>
    Array.isArray(v) ? v[v.length - 1] : v;

const num = (v: string | string[] | undefined, fallback: number): number => {
    const s = single(v);
    const n = Number(s);
    return s !== undefined && s !== '' && !isNaN(n) ? n : fallback;
};

//  Empty tokens are dropped BEFORE Number(), because Number('') is 0, not NaN.
//  Without this, an absent param yields [0] instead of [] and every round trip
//  and "clean URL" assertion fails. No `n >= 0` filter: ItemSearchPartSelector
//  encodes cash parts as negative ids (`-(id + 1)`), so a `?parts=-10` URL
//  must round-trip back to `parts: [-10]`, not silently drop it.
const list = (v: string | string[] | undefined): number[] =>
    (single(v) ?? '')
        .split(',')
        .map((t) => t.trim())
        .filter((t) => t !== '')
        .map(Number)
        .filter((n) => !isNaN(n));

export function filterToQuery(f: IBrowseFilter): Record<string, string> {
    const q: Record<string, string> = {};
    if (f.name) q.name = f.name;
    if (f.minLevel !== 0) q.lmin = String(f.minLevel);
    if (f.maxLevel !== 100) q.lmax = String(f.maxLevel);
    if (f.ranks.length) q.g = f.ranks.join(',');
    if (f.categories.length) q.c = f.categories.join(',');
    if (f.parts.length) q.parts = f.parts.join(',');
    if (f.types.length) q.t = f.types.join(',');
    if (f.sort !== 'relevance') q.sort = f.sort;
    if (f.dir !== defaultDir(f.sort)) q.dir = f.dir;
    if (f.density !== 'card') q.density = f.density;
    return q;
}

export function defaultDir(sort: SortKey): SortDir {
    return sort === 'relevance' ? 'desc' : 'asc';
}

export function queryToFilter(q: Record<string, string | string[] | undefined>): IBrowseFilter {
    const sortRaw = single(q.sort);
    const sort: SortKey = sortRaw === 'name' || sortRaw === 'id' ? sortRaw : 'relevance';
    const dirRaw = single(q.dir);
    const dir: SortDir = dirRaw === 'asc' || dirRaw === 'desc' ? dirRaw : defaultDir(sort);
    const densityRaw = single(q.density);
    const density: Density =
        densityRaw === 'row' || densityRaw === 'table' ? densityRaw : 'card';
    return {
        name: single(q.name) ?? '',
        minLevel: num(q.lmin, 0),
        maxLevel: num(q.lmax, 100),
        ranks: list(q.g),
        categories: list(q.c),
        parts: list(q.parts),
        types: list(q.t),
        sort, dir, density,
    };
}
